<template>
  <div class="page-container">
    <!-- Loading 遮罩 -->
    <div v-if="initialLoading" class="loading-overlay">
      <div class="loading-spinner"></div>
    </div>

    <!-- 視頻區域 -->
    <div class="video-area" v-show="!initialLoading">
      <video autoplay muted ref="videoPlayer" :loop="isSpeaking">
        <source :src="currentVideo" type="video/mp4">
      </video>
    </div>

    <!-- 標題區域 -->
    <div class="title-area">
      <h2>AI智能客服</h2>
    </div>

    <!-- 聊天室區域 -->
    <div class="chat-container">
      <!-- 聊天訊息區域 -->
      <div class="messages-area" ref="messagesArea">
        <div
          v-for="(message, index) in messages"
          :key="index"
          :class="['message', message.type]"
        >
          <div class="message-content">
            <template v-if="message.isLoading">
              正在思考中<span class="loading-dots"></span>
            </template>
            <template v-else>
              {{ message.content }}
            </template>
          </div>
        </div>
      </div>

      <!-- 添加語言選擇區域 -->
      <div class="language-selector">
        <img 
          src="@/assets/selectlang.png" 
          @click="toggleLanguageMenu"
          class="language-icon"
        >
        <div v-if="showLanguageMenu" class="language-dropdown">
          <div class="language-option" @click="selectLanguage('zh-TW')">繁體中文</div>
          <div class="language-option" @click="selectLanguage('en')">English</div>
          <div class="language-option" @click="selectLanguage('ja')">日本語</div>
        </div>
      </div>

      <!-- 輸入區域 -->
      <div class="input-area">
        <input
          v-model="newMessage"
          @keyup.enter="sendMessage"
          type="text"
          placeholder="請輸入訊息..."
          :disabled="isLoading"
        />
        <button @click="sendMessage" :disabled="isLoading">
          {{ isLoading ? '發送中...' : '發送' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useRoute } from 'vue-router'
import { api, handleApiError } from '@/api/api'  // 引入 API
import { speechService, handleSpeechError } from '@/api/speech'

export default {
  name: "ChatView",
  data() {
    const route = useRoute()
    return {
      messages: [
        {
          content: "您好，我是AI虛擬解說員，歡迎向我提問關於這個地方的任何問題~",
          type: "received"
        }
      ],
      newMessage: "",
      showLanguageMenu: false,
      selectedLanguage: 'zh-TW',
      param: route.params.param,
      knowledgeData: null,  // 添加新的數據屬性
      isLoading: false,
      initialLoading: true,
      isSpeaking: false,
      audioDuration: 0,
      currentVideo: new URL('@/assets/startM.mp4', import.meta.url).href,
      replayVideo: new URL('@/assets/replayM.mp4', import.meta.url).href,
    };
  },
  methods: {
    scrollToBottom() {
      setTimeout(() => {
        const messagesArea = this.$refs.messagesArea;
        messagesArea.scrollTop = messagesArea.scrollHeight;
      }, 100); // 添加小延遲確保內容已更新
    },
    async sendMessage() {
      if (!this.newMessage.trim() || this.isLoading) return;
      
      this.isLoading = true;
      
      this.messages.push({
        content: this.newMessage,
        type: "sent"
      });

      const loadingMessageIndex = this.messages.length;
      this.messages.push({
        content: "正在思考中",
        type: "received",
        isLoading: true
      });

      try {
        const response = await api.chat.sendMessage(
          this.param,
          this.newMessage
        );

        if (response.data.success) {
          const aiResponse = response.data.data['content'];
          this.messages[loadingMessageIndex] = {
            content: aiResponse,
            type: "received",
            isLoading: false
          };
          
          this.scrollToBottom();
          
          // 將文字轉語音作為獨立事件處理
          this.handleTextToSpeech(aiResponse);
        }
      } catch (error) {
        console.error('發送消息失敗:', handleApiError(error));
        this.messages[loadingMessageIndex] = {
          content: "抱歉，發送消息時出現錯誤",
          type: "received"
        };
        this.scrollToBottom();
      } finally {
        this.isLoading = false;
        this.newMessage = "";
      }
    },
    async handleTextToSpeech(text) {
      if (speechService && typeof speechService.textToSpeech === 'function') {
        try {
          this.isSpeaking = true;
          // 切換到重複播放的視頻
          this.currentVideo = this.replayVideo;
          this.$refs.videoPlayer.load();
          this.$refs.videoPlayer.play();
          
          const { duration } = await speechService.textToSpeech(
            text, 
            this.selectedLanguage,
            // 添加回調函數
            () => {
              //console.log('在 ChatView 中收到語音播放結束事件');
              this.isSpeaking = false;
              // 語音結束後切換回開始視頻並停止播放
              this.currentVideo = new URL('@/assets/replayM.mp4', import.meta.url).href;
              this.$refs.videoPlayer.load();
              // 停止視頻循環播放
              this.$refs.videoPlayer.loop = false;
              // 暫停視頻播放
              this.$refs.videoPlayer.pause();
            }
          );
          
          this.audioDuration = duration;
          
        } catch (speechError) {
          console.error('文字轉語音失敗:', handleSpeechError(speechError));
        }
      }
    },
    stopSpeaking() {
      if (speechService && typeof speechService.stopSpeaking === 'function') {
        speechService.stopSpeaking();
        this.isSpeaking = false;
        // 停止播放時也切換回開始視頻
        this.currentVideo = new URL('@/assets/replayM.mp4', import.meta.url).href;
        this.$refs.videoPlayer.load();
  
      }
    },
    toggleLanguageMenu() {
      this.showLanguageMenu = !this.showLanguageMenu;
    },
    selectLanguage(lang) {
      this.selectedLanguage = lang;
      this.showLanguageMenu = false;
      // 這裡可以添加切換語言的邏輯
    },
    startVideo() {
      const video = this.$refs.videoPlayer;
      if (video) {
        video.play();
      }
    }
  },
  async created() {
    try {
      const response = await api.getknowledge.getKnowledge(this.param);
      this.knowledgeData = response.data;
      if (response.data.success && response.data.data) {
        localStorage.setItem('token', response.data.data);
      }
    } catch (error) {
      console.error('獲取知識庫失敗:', handleApiError(error));
    } finally {
      this.initialLoading = false;
      // 在下一個 tick 開始播放視頻
      this.$nextTick(() => {
        this.startVideo();
      });
    }
  },
  updated() {
    // 當內容更新時也自動捲動到底部
    this.scrollToBottom();
  }
};
</script>

<style scoped>
.page-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.video-area {
  height: 50vh;
  width: 100%;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #000; /* 添加黑色背景 */
  opacity: 0;
  transition: opacity 0.3s ease;
}

.video-area:not([v-show="false"]) {
  opacity: 1;
}

.video-area video {
  max-width: 414px; /* 與聊天室容器最大寬度一致 */
  height: 100%;
  object-fit: contain; /* 改為 contain 以保持視頻原始比例 */
}

.title-area {
  background-color: #666666;
  padding: 8px;
}

.title-area h2 {
  color: white;
  font-weight: bold;
  margin: 0;
  font-size: 1.1rem;
  max-width: 414px;
  margin: 0 auto;
}

.chat-container {
  height: calc(50vh - 37px); /* 減去標題區域的高度 */
  position: relative;
  max-width: 600px;
  margin: 0 auto;
  background: #fff;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.messages-area {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  background: #f5f5f5;
  -webkit-overflow-scrolling: touch;
}

.message {
  margin: 8px 0;
  padding: 8px 12px;
  border-radius: 16px;
  max-width: 85%;
  word-break: break-word;
  clear: both; /* 確保每條消息獨占一行 */
}

.sent {
  background: gray;
  color: white;
  float: right; /* 改為靠右 */
  border-bottom-right-radius: 4px; /* 修改圓角位置 */
  border-bottom-left-radius: 16px; /* 恢復原始圓角 */
}

.received {
  background: white;
  color: #000;
  float: left; /* 改為靠左 */
  border-bottom-left-radius: 4px; /* 修改圓角位置 */
  border-bottom-right-radius: 16px; /* 恢復原始圓角 */
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.message-content {
  display: inline-block; /* 確保內容正確換行 */
}

.input-area {
  display: flex;
  gap: 8px;
  padding: 10px;
  background: white;
  position: sticky;
  bottom: 0;
  width: 100%;
  box-sizing: border-box;
}

input {
  flex: 1;
  padding: 12px;
  border: none;
  font-size: 16px;
  background: white;
  outline: none; /* 移除點擊時的外框 */
}

button {
  padding: 8px 16px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 16px;
  min-width: 70px;
}

.language-selector {
  position: fixed;        /* 固定定位 */
  bottom: 80px;          /* 距離底部的距離 */
  left: 50%;             /* 水平置中 */
  transform: translateX(-50%); /* 確保完全置中 */
  z-index: 1000;         /* 確保顯示在其他元素上方 */
  background: transparent;/* 背景透明 */
  padding: 8px;
}

.language-icon {
  width: 40px;
  height: 40px;
  cursor: pointer;
  display: block;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2)); /* 陰影效果 */
}

.language-dropdown {
  position: fixed;        
  top: 50%;              /* 垂直置中 */
  left: 50%;             /* 水平置中 */
  transform: translate(-50%, -150%); /* 完全置中 */
  background: white;
  border-radius: 8px;    /* 增加圓角 */
  box-shadow: 0 2px 10px rgba(0,0,0,0.2); /* 增強陰影效果 */
  z-index: 1001;         
  width: 200px;          
  text-align: center;    
}

.language-option {
  padding: 15px 20px;    
  cursor: pointer;
  white-space: nowrap;
  border-bottom: 1px solid #f5f5f5;
}

.language-option:last-child {
  border-bottom: none;   
}

.language-option:hover {
  background: #f5f5f5;
}

@media (min-width: 600px) {
  .chat-container {
    max-width: 414px;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  }
  
  .video-area {
    display: flex;
    justify-content: center;
  }
}

/* 添加動畫樣式 */
.loading-dots::after {
  content: '';
  animation: dots 1.5s steps(4, end) infinite;
}

@keyframes dots {
  0%, 20% { content: ''; }
  40% { content: '.'; }
  60% { content: '..'; }
  80%, 100% { content: '...'; }
}

/* 或者使用旋轉動畫版本 */
.loading-dots-spin {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid #666;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s linear infinite;
  margin-left: 8px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

button:disabled {
  background: #cccccc;
  cursor: not-allowed;
}

input:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #666666;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
